import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { tags as tagsApi } from "shared/api";
import TagGroup from "./TagGroup";
import styles from "./TagsItem.module.css";

/** The groups the editor renders, in display order. */
export const TAG_GROUPS = [
  { type: "language", label: "Languages" },
  { type: "keyword", label: "Keyword Tags" },
  { type: "searchable", label: "Searchable Tags" },
  { type: "internal", label: "Internal Tags" },
];

const DEFAULT_TYPE = "keyword";

const knownType = (type) =>
  TAG_GROUPS.some((g) => g.type === type) ? type : DEFAULT_TYPE;

const TagsItem = ({ title, selectedTags, onChange }) => {
  const [allTags, setAllTags] = useState([]);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    tagsApi
      .list()
      .then((rows) => {
        if (cancelled) return;
        setAllTags(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** name -> group, so a selected tag lands under the right heading. */
  const typeByName = useMemo(() => {
    const map = new Map();
    allTags.forEach((t) => {
      if (t && t.name) map.set(t.name.toLowerCase(), knownType(t.type));
    });
    return map;
  }, [allTags]);

  const selected = useMemo(() => selectedTags || [], [selectedTags]);

  const groups = useMemo(() => {
    const selectedByType = {};
    const availableByType = {};
    TAG_GROUPS.forEach(({ type }) => {
      selectedByType[type] = [];
      availableByType[type] = [];
    });

    const chosen = new Set(selected.map((n) => n.toLowerCase()));

    selected.forEach((name) => {
      // A tag the registry has never heard of still has to appear somewhere.
      const type = typeByName.get(name.toLowerCase()) || DEFAULT_TYPE;
      selectedByType[type].push(name);
    });

    allTags.forEach((t) => {
      if (!t || !t.name) return;
      if (chosen.has(t.name.toLowerCase())) return;
      availableByType[knownType(t.type)].push(t.name);
    });

    return { selectedByType, availableByType };
  }, [allTags, selected, typeByName]);

  const setSelection = (next) => {
    if (onChange) onChange(next);
  };

  const handleRemove = (name) => {
    setSelection(selected.filter((n) => n !== name));
  };

  const handleAdd = (type) => (name) => {
    if (selected.some((n) => n.toLowerCase() === name.toLowerCase())) return;

    const existing = allTags.find(
      (t) => t.name && t.name.toLowerCase() === name.toLowerCase()
    );
    if (existing) {
      setSelection([...selected, existing.name]);
      return;
    }

    setCreating(true);
    tagsApi
      .create({ name, type })
      .then((created) => {
        const createdName = (created && created.name) || name;
        const createdType = knownType((created && created.type) || type);
        setAllTags((prev) =>
          prev.some(
            (t) => t.name && t.name.toLowerCase() === createdName.toLowerCase()
          )
            ? prev
            : [...prev, { ...created, name: createdName, type: createdType }]
        );
        setSelection([...selected, createdName]);
      })
      .catch((err) => setError(err))
      .finally(() => setCreating(false));
  };

  return (
    <div className={styles.section}>
      {title && <div className={styles.sectionTitle}>{title}</div>}
      {error && <div className={styles.errorText}>Couldn't load tags.</div>}
      {TAG_GROUPS.map(({ type, label }) => (
        <TagGroup
          key={type}
          label={label}
          selected={groups.selectedByType[type]}
          available={groups.availableByType[type]}
          onAdd={handleAdd(type)}
          onRemove={handleRemove}
          creating={creating}
        />
      ))}
    </div>
  );
};

TagsItem.propTypes = {
  title: PropTypes.string,
  selectedTags: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
};

TagsItem.defaultProps = {
  title: "Tags",
  selectedTags: [],
  onChange: undefined,
};

export default TagsItem;
